import { useEffect } from "react";
import RsvpModal from "./RsvpModal";
import ChoiceScreen from "./ChoiceScreen";
import CodeEntryScreen from "./CodeEntryScreen";
import CodedFlow from "./CodedFlow";
import RequestFlow from "./RequestFlow";
import useRsvpFlow from "./useRsvpFlow";
import { trackRsvpStarted } from "../../lib/analytics";

// Once-per-invitation-page-session guard for rsvp_started (Browser-owned RSVP
// intent). Module-scoped so it survives React StrictMode's double-invoked
// effect, component remounts, and close→reopen of the same invitation's modal;
// keyed by event_id — mirroring the invitation_opened guard in pages/RSVP.jsx.
// See docs/analytics/event-ownership.md §7.
const rsvpStartedFired = new Set();

// ─── Component ─────────────────────────────────────────────────

/**
 * RsvpDialog
 *
 * High-level RSVP entry point. Owns the flow hook and selects which
 * flow screen renders inside the modal shell. Consumers (CtaSection)
 * just pass `open`, `onClose`, `eventId`, `rsvpCode`, and `eventType`.
 */
const RsvpDialog = ({ open, onClose, eventId, rsvpCode, eventType }) => {
  const flow = useRsvpFlow(open, { eventId, rsvpCode });

  // Reset all flow state whenever the modal transitions open→closed so
  // the next open starts fresh. We do it on close (not on open) so the
  // user can't see the state flicker through reset before the exit.
  useEffect(() => {
    if (!open) flow.actions.reset();
    // We intentionally depend only on `open` — including `flow.actions`
    // would re-run this every render since the actions object is rebuilt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // rsvp_started (Browser-owned INTENT) — the guest opened the RSVP flow. The
  // authoritative UI transition is the modal going open (false→true); we emit
  // once per invitation page session. The module-scoped guard keyed by event_id
  // collapses StrictMode's double-invoke, remounts, and close→reopen into a
  // single emission — the same de-dup pattern as signup_started /
  // invitation_opened. NOT the RSVP truth: the authoritative rsvp_submitted /
  // rsvp_requested are Backend-owned (see docs/analytics/event-ownership.md §7).
  useEffect(() => {
    if (!open || !eventId) return;
    if (rsvpStartedFired.has(eventId)) return;
    rsvpStartedFired.add(eventId);
    trackRsvpStarted({ eventId, eventType });
  }, [open, eventId, eventType]);

  const handleClose = () => onClose?.();

  const content = (() => {
    switch (flow.mode) {
      case "choice":
        return (
          <ChoiceScreen
            onChooseCode={() => flow.actions.changeMode("codeEntry")}
            onChooseRequest={() => flow.actions.changeMode("request")}
          />
        );
      case "codeEntry":
        return (
          <CodeEntryScreen
            value={flow.manualCode}
            onChange={flow.actions.setManualCode}
            onBack={() => flow.actions.changeMode("choice")}
            onSubmit={flow.actions.submitManualCode}
            error={flow.error}
            submitting={flow.submitting}
          />
        );
      case "request":
        return (
          <RequestFlow
            flow={flow}
            onBack={() => flow.actions.changeMode("choice")}
            onClose={handleClose}
          />
        );
      case "code":
      default:
        return <CodedFlow flow={flow} onClose={handleClose} />;
    }
  })();

  return (
    <RsvpModal
      open={open}
      onClose={handleClose}
      submitting={flow.submitting}
      hasUnsavedProgress={flow.hasUnsavedProgress}
    >
      {content}
    </RsvpModal>
  );
};

export default RsvpDialog;
